// src/components/ProtectedRoute.tsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { isTokenExpired } from "../api/auth";
import { getEnvVar } from "../api/config";
import PageLoader from "./PageLoader";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading, hasHydrated, token } = useAuth();
  const location = useLocation();

  // Feature flag: require email verification (default true)
  // Build-time only: VITE_REQUIRE_EMAIL_VERIFICATION ('false' disables)
  const requireEmailVerification = (() => {
    // Source using central accessor so Infisical/Pages/CI injections are honored
    const raw = getEnvVar('VITE_REQUIRE_EMAIL_VERIFICATION', 'true');
    // Debug
    try {
      console.log('[ProtectedRoute] VITE_REQUIRE_EMAIL_VERIFICATION (raw via getEnvVar):', raw);
    } catch {}
    // Treat 'false' and '0' (any case) as disabled
    const val = typeof raw === 'string' ? raw.toLowerCase() : String(raw);
    const required = !(val === 'false' || val === '0');
    try { console.log('[ProtectedRoute] requireEmailVerification (computed):', required); } catch {}
    return required;
  })();

  // Wait for auth store to hydrate from localStorage
  if (!hasHydrated) {
    return <PageLoader />;
  }

  if (isLoading) {
    return <PageLoader />;
  }

  // If a token exists but is expired, redirect to login (store will be cleared by interceptors/App check)
  if (hasHydrated && token && isTokenExpired(token)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Guard against race: token valid but user profile not loaded yet
  // Avoid rendering children or mis-routing based on incomplete user state
  if (isAuthenticated() && !user) {
    return <PageLoader />;
  }

  if (!isAuthenticated()) {
    // Save the location the user was trying to access for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for email verification first - only if authenticated and required
  if (
    requireEmailVerification &&
    user &&
    user.isVerified === false &&
    !location.pathname.startsWith("/verify-email")
  ) {
    console.log(
      "ProtectedRoute: Email verification required and user not verified; redirecting to /verify-email-optional. Flag requireEmailVerification=",
      requireEmailVerification,
      " Current user:",
      user
    );
    return (
      <Navigate
        to="/verify-email-optional"
        state={{ from: location, email: user.email }}
        replace
      />
    );
  }

  // Check for profile completion - only if authenticated and email verified
  if (user && user.isVerified && user.profile_exists === false && !location.pathname.startsWith("/onboarding")) {
    console.log("ProtectedRoute: User profile incomplete, redirecting to /onboarding. Current user state:", user);
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
